import { memo, useCallback } from "react";
import type React from "react";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import OutlinedInput from "@mui/material/OutlinedInput";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import { styled } from "../../../../lib/styled";

export interface SearchBarProps {
  /** Current search text — caller owns the state. */
  value: string;
  /** Called on every keystroke with the new raw value. */
  onChange: (value: string) => void;
  placeholder?: string;
  /** Accessible label for the input. */
  ariaLabel?: string;
}

const SearchRoot = styled("div")({
  paddingTop: 8,
  paddingBottom: 8,
});

const SearchInput = styled(OutlinedInput)({
  width: "100%",
});

/** Controlled search input with a leading search icon and a clear button (shown when non-empty).
 *  No internal debouncing — use `useDebouncedValue` at the call site for the API query. */
export const SearchBar = memo(function SearchBar({
  value,
  onChange,
  placeholder = "Search…",
  ariaLabel = "Search",
}: SearchBarProps) {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value),
    [onChange]
  );
  const handleClear = useCallback(() => onChange(""), [onChange]);

  return (
    <SearchRoot>
      <SearchInput
        size="small"
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        inputProps={{ "aria-label": ariaLabel }}
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        }
        endAdornment={
          value ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={handleClear} aria-label="Clear search">
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : undefined
        }
      />
    </SearchRoot>
  );
});
